/**
 * 예산과목 구조·과목 등록 PoC 저장소 (tenant별 localStorage)
 *
 * ## 예산구분 (budgetType)
 * 전체(조회) · 사업예산 · 자본예산. 예비비 등 **동일 과목코드**가 사업/자본에 있을 수 있어
 * 저장·확정 키에는 사업/자본만 쓰고, 검색 ‘전체’는 양쪽 과목을 병합 조회한다.
 * - 사업예산: 수입 관 600대 / 지출 관 700대 (+사업 예비비)
 * - 자본예산: 수입 관 100대 / 지출 관 200대 (+자본 예비비)
 *
 * ## 목적구분 (purpose)
 * - 보고: 예산서·상급기관 제출 등 대외·보고용 체계 (흔히 더 얕음, 예: 세목까지)
 * - 집행: 품의·원인행위·지출결의 등 내부 집행용 체계 (흔히 더 깊음, 예: 세세목까지)
 * 동일 기관·연도·수지라도 보고/집행을 각각 확정할 수 있다.
 *
 * ## 확정 잠금
 * 확정 후 레벨 행/속성을 바꾸면 이미 열린 편성·지출 화면·테이블 컬럼과 어긋난다.
 * 확정자·확정일시·버전은 “누가 언제 확정본을 냈는지” 감사 정보다.
 *
 * ## 확정해제
 * 같은 목적(보고 또는 집행)으로 예산과목이 1건이라도 있으면 해제 불가.
 * (하류 데이터가 확정 구조를 전제로 하므로) → 차기년도 복사 사용.
 *
 * ## 분개매핑
 * 구조에서는 분개 가능 레벨을 여러 개 둘 수 있다.
 * 실제 과목–계정 매핑 시 상위(예: 목)가 분개되면 하위(세목) 분개를 막는 방식.
 *
 * ## 하류 저장
 * UI 동적 컬럼 + DB는 최종/분개 과목코드·스냅샷 (가변 DDL 지양)
 * @see D:\295\erp\BD\design\02_예산과목_구조_확정계약.md (SSOT)
 * @see docs/BUDGET_SUBJECT_STRUCTURE_CONTRACT.md (FE 포인터)
 */

import { ensureSubjectsIdentity } from 'budget/budgetSubjectHistoryStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const STRUCTURE_STORAGE_PREFIX = 'budgetStructure';
export const SUBJECT_STORAGE_PREFIX = 'budgetSubject';

const STRUCTURE_SAVE_QUERY = [
  'BdSubject.insertSubjectStructure',
  'BdSubject.updateSubjectStructure',
  'BdSubject.deleteSubjectStructure',
];
const SUBJECT_SAVE_QUERY = [
  'BdSubject.insertSubject',
  'BdSubject.updateSubject',
  'BdSubject.deleteSubject',
];

const structureCache = new Map();
const subjectCache = new Map();

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

/** 표준코드 출처(기관/부처) — 과목코드 자리는 예산과목등록에서 관리 */
export const RULE_OPTIONS = [
  { value: '행정안전부', label: '행정안전부' },
  { value: '보건복지부', label: '보건복지부' },
  { value: '설립지자체', label: '설립지자체' },
  { value: '자체', label: '자체' },
  { value: '기타', label: '기타' },
];

/**
 * 목적구분
 * - 보고(예산서): 대외·상급 제출용. 보통 얕은 체계.
 * - 내부집행: 편성·배정·품의·원인행위·지출결의용. 보통 더 깊은 체계.
 */
export const PURPOSE_OPTIONS = [
  { value: '보고', label: '보고(예산서)' },
  { value: '집행', label: '내부집행' },
];

export const PURPOSE_HELP = {
  보고:
    '예산서·결산서 등 상급기관·대외 제출에 쓰는 과목 체계입니다. 보고에 필요한 깊이까지만 둡니다. (예: 세목까지 보고하고 내부는 더 세분)',
  집행:
    '편성·배정·품의·원인행위·지출결의 등 기관 내부 집행에 쓰는 과목 체계입니다. 보고보다 깊게 둘 수 있습니다. (예: 세세목·세세세목)',
};

export const PURPOSE_CODE = {
  보고: 'REPORT',
  집행: 'EXEC',
};

export const LEVEL_CODE_OPTIONS = [
  { value: 'LV1', label: 'LV1' },
  { value: 'LV2', label: 'LV2' },
  { value: 'LV3', label: 'LV3' },
  { value: 'LV4', label: 'LV4' },
  { value: 'LV5', label: 'LV5' },
  { value: 'LV6', label: 'LV6' },
  { value: 'LV7', label: 'LV7' },
  { value: 'LV8', label: 'LV8' },
];

export const INOUT_OPTIONS = [
  { value: '지출', label: '지출' },
  { value: '수입', label: '수입' },
];

export const BUDGET_TYPE_ALL = '전체';
export const BUDGET_TYPE_BUSINESS = '사업예산';
export const BUDGET_TYPE_CAPITAL = '자본예산';

/** 실제 저장·확정에 쓰는 예산구분 (전체 제외) */
export const BUDGET_TYPE_CONCRETE = [BUDGET_TYPE_BUSINESS, BUDGET_TYPE_CAPITAL];

export const BUDGET_TYPE_OPTIONS = [
  { value: BUDGET_TYPE_ALL, label: '전체' },
  { value: BUDGET_TYPE_BUSINESS, label: '사업예산' },
  { value: BUDGET_TYPE_CAPITAL, label: '자본예산' },
];

/** 그리드 등에서 예산구분 선택(전체 제외) */
export const BUDGET_TYPE_ROW_OPTIONS = [
  { value: BUDGET_TYPE_BUSINESS, label: '사업예산' },
  { value: BUDGET_TYPE_CAPITAL, label: '자본예산' },
];

/** 검색조건 기본값 */
export const BUDGET_TYPE_DEFAULT = BUDGET_TYPE_ALL;

/** 저장 키 폴백 (전체로 키를 만들지 않음) */
export const STRUCTURE_BUDGET_TYPE = BUDGET_TYPE_BUSINESS;

/**
 * 사업예산·자본예산은 과목코드(예: 예비비 800·801)가 동일할 수 있다.
 * 반드시 예산구분(budgetType)으로 분리해 저장·확정한다. 조회만 ‘전체’ 가능.
 */
export const BUDGET_TYPE_HELP =
  '사업예산(관 600·700대)과 자본예산(관 100·200대)을 구분합니다. 예비비 등 동일 과목코드가 양쪽에 있으므로 행마다 예산구분이 필요합니다. 검색 ‘전체’는 양쪽을 함께 조회합니다.';

export function isBudgetTypeAll(budgetType) {
  return !budgetType || budgetType === BUDGET_TYPE_ALL;
}

export function resolveBudgetTypes(budgetType) {
  if (isBudgetTypeAll(budgetType)) return [...BUDGET_TYPE_CONCRETE];
  return [budgetType];
}

export const DOWNSTREAM_IMPACT_SCREENS = [
  { id: 'budget_plan', label: '예산편성' },
  { id: 'budget_alloc', label: '예산배정' },
  { id: 'purchase_req', label: '품의' },
  { id: 'obligation', label: '원인행위' },
  { id: 'expense_resol', label: '지출결의' },
  { id: 'subject_reg', label: '예산과목등록' },
];

/** 확정 잠금·감사·해제에 대한 화면 안내문 */
export const LOCK_HELP = {
  confirm:
    '구조확정: 선택한 목적(보고 또는 집행)의 레벨 체계를 고정합니다. 확정되면 편성·배정·품의·원인행위·지출결의·과목등록의 과목 컬럼이 이 체계를 따릅니다. 레벨 행 추가·삭제·명칭 변경이 잠깁니다.',
  audit:
    '버전 · 확정자 · 확정일시는 “이 확정본을 누가 언제 냈는지” 남기는 감사 정보입니다. 차기년도 복사 시 버전이 올라갑니다.',
  unconfirm:
    '확정해제: 잠금을 풀어 다시 수정합니다. 같은 목적으로 이미 예산과목을 등록했다면 하위 데이터가 깨질 수 있어 해제할 수 없습니다(0건일 때만 가능). 과목이 있으면 차기년도 복사를 사용하세요.',
};

/**
 * 프리셋 — 보고는 분개 후보를 목에, 집행은 세목에 (여러 분개레벨 허용의 예시)
 */
export const STRUCTURE_PRESETS = {
  income: [
    { levelNo: 1, useYn: 'Y', levelCode: 'LV1', levelName: '관', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 2, useYn: 'Y', levelCode: 'LV2', levelName: '항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 3, useYn: 'Y', levelCode: 'LV3', levelName: '세항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 4, useYn: 'Y', levelCode: 'LV4', levelName: '목', finalYn: 'Y', codeRule: '행정안전부', acctMapYn: 'Y' },
    { levelNo: 5, useYn: 'N', levelCode: 'LV5', levelName: '', finalYn: 'N', codeRule: '자체', acctMapYn: 'N' },
  ],
  expense_report: [
    { levelNo: 1, useYn: 'Y', levelCode: 'LV1', levelName: '관', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 2, useYn: 'Y', levelCode: 'LV2', levelName: '항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 3, useYn: 'Y', levelCode: 'LV3', levelName: '세항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 4, useYn: 'Y', levelCode: 'LV4', levelName: '목', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'Y' },
    { levelNo: 5, useYn: 'Y', levelCode: 'LV5', levelName: '세목', finalYn: 'Y', codeRule: '행정안전부', acctMapYn: 'N' },
  ],
  expense_exec: [
    { levelNo: 1, useYn: 'Y', levelCode: 'LV1', levelName: '관', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 2, useYn: 'Y', levelCode: 'LV2', levelName: '항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 3, useYn: 'Y', levelCode: 'LV3', levelName: '세항', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'N' },
    { levelNo: 4, useYn: 'Y', levelCode: 'LV4', levelName: '목', finalYn: 'N', codeRule: '행정안전부', acctMapYn: 'Y' },
    { levelNo: 5, useYn: 'Y', levelCode: 'LV5', levelName: '세목', finalYn: 'Y', codeRule: '행정안전부', acctMapYn: 'Y' },
  ],
};

export function clonePreset(inoutDiv, purpose = '집행') {
  let key = 'expense_exec';
  if (inoutDiv === '수입') key = 'income';
  else if (purpose === '보고') key = 'expense_report';
  else key = 'expense_exec';
  return JSON.parse(JSON.stringify(STRUCTURE_PRESETS[key]));
}

/** 과목구조 키: 기관·연도·수지·목적 (예산구분·적용기간 비사용) */
export function structureStorageKey({ tenantCode, fiscalYear, inoutDiv, purpose }) {
  return [
    STRUCTURE_STORAGE_PREFIX,
    tenantCode || '',
    fiscalYear || '',
    inoutDiv || '',
    purpose || '집행',
  ].join('|');
}

/** PoC 이전 키(예산구분 포함) 호환 */
function legacyStructureStorageKeys({ tenantCode, fiscalYear, inoutDiv, purpose, budgetType }) {
  const purposeVal = purpose || '집행';
  const types =
    budgetType && !isBudgetTypeAll(budgetType)
      ? [budgetType]
      : [STRUCTURE_BUDGET_TYPE, BUDGET_TYPE_CAPITAL];
  const keys = [];
  types.forEach((bt) => {
    keys.push(
      [STRUCTURE_STORAGE_PREFIX, tenantCode || '', fiscalYear || '', inoutDiv || '', purposeVal, bt].join('|')
    );
    // purpose 없이 저장됐던 구키
    if (!purpose || purpose === '집행') {
      keys.push(
        [STRUCTURE_STORAGE_PREFIX, tenantCode || '', fiscalYear || '', inoutDiv || '', bt].join('|')
      );
    }
  });
  return keys;
}

/** 과목 저장 키 — 행(예산구분) 단위로 분리 유지 */
export function subjectStorageKey({ tenantCode, fiscalYear, inoutDiv, purpose, budgetType }) {
  const bt = isBudgetTypeAll(budgetType) ? STRUCTURE_BUDGET_TYPE : budgetType || STRUCTURE_BUDGET_TYPE;
  return [
    SUBJECT_STORAGE_PREFIX,
    tenantCode || '',
    fiscalYear || '',
    inoutDiv || '',
    purpose || '집행',
    bt,
  ].join('|');
}

export function createStructureUid(prefix = 'SS') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** DATETIME(3) 컬럼용 — FE ISO 문자열 변환 */
function toSqlTimestamp(value) {
  if (!value) return null;
  const s = String(value);
  if (!s.includes('T')) return s;
  return s.replace('T', ' ').replace(/Z$/, '');
}

export function resolveLevelNo(row) {
  const m = String(row?.levelCode || '').match(/LV(\d+)/i);
  if (m) return Number(m[1]);
  return Number(row?.levelNo) || 0;
}

export function getUsedLevels(rows = []) {
  return (rows || [])
    .filter((r) => r.useYn === 'Y' && String(r.levelName || '').trim() !== '')
    .map((r) => ({ ...r, levelNo: resolveLevelNo(r) || Number(r.levelNo) || 0 }))
    .sort((a, b) => Number(a.levelNo) - Number(b.levelNo));
}

/**
 * CRUD 그리드 행 정규화
 * - 삭제(D) 행 제외
 * - mod / _tempId 제거 (저장·재조회 시 상태 아이콘이 남지 않도록)
 */
export function normalizeStructureGridRows(rows = []) {
  return (rows || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => {
      const { mod, _tempId, ...rest } = r;
      return rest;
    });
}

/**
 * 구조 검증
 * - 레벨 연속, 최종레벨 1건, 레벨명
 * - 분개매핑은 다건 허용 (상위·하위 동시 분개 차단은 과목–계정 매핑 단계에서 처리)
 */
export function validateStructure(rows = []) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const used = getUsedLevels(active);
  const finalCnt = used.filter((r) => r.finalYn === 'Y').length;
  const levels = used.map((r) => Number(r.levelNo));
  const continuous = levels.length > 0 && levels.every((v, idx) => v === idx + 1);
  const hasNames = used.every((r) => String(r.levelName).trim() !== '');
  const acctMapLevels = used.filter((r) => r.acctMapYn === 'Y');

  return {
    used,
    continuous,
    finalOne: finalCnt === 1,
    hasNames,
    canConfirm: continuous && finalCnt === 1 && hasNames && used.length > 0,
    previewText: used.length ? used.map((r) => r.levelName).join(' > ') : '구조 없음',
    finalLevelName: used.find((r) => r.finalYn === 'Y')?.levelName || '',
    acctMapLevelNames: acctMapLevels.map((r) => r.levelName),
  };
}

function readStructureFromStorage(dims) {
  try {
    const primary = structureStorageKey(dims);
    let raw = localStorage.getItem(primary);
    if (!raw) {
      for (const key of legacyStructureStorageKeys(dims || {})) {
        raw = localStorage.getItem(key);
        if (raw) break;
      }
    }
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** API 행 → 화면 payload (levels_json ↔ rows/usedLevels, confirm_yn ↔ status) */
function mapApiRowToStructurePayload(row, dims) {
  let rows = [];
  try {
    const parsed = row?.levelsJson ? JSON.parse(row.levelsJson) : null;
    rows = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.rows) ? parsed.rows : [];
  } catch {
    rows = [];
  }
  return {
    structureUid: row?.structureUid || '',
    dims: { ...dims },
    purpose: row?.purpose || dims?.purpose || '집행',
    rows,
    usedLevels: getUsedLevels(rows),
    status: row?.confirmYn === 'Y' ? '확정' : '작성중',
    versionNo: Number(row?.versionNo) || 1,
    confirmedAt: row?.confirmedAt || null,
    confirmedBy: row?.confirmedBy || null,
    finalLevelName: rows.find((r) => r.finalYn === 'Y')?.levelName || '',
    applyLevelName: row?.applyLevelName || '',
  };
}

function structureCacheKey(dims) {
  return structureStorageKey(dims);
}

/** 동기 getter용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedStructurePayload(dims) {
  const key = structureCacheKey(dims);
  if (structureCache.has(key)) return structureCache.get(key);
  if (isBdApiEnabled()) return null;
  const payload = readStructureFromStorage(dims);
  structureCache.set(key, payload);
  return payload;
}

export async function loadStructurePayload(dims) {
  const key = structureCacheKey(dims);
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdSubject.selectSubjectStructureList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      inoutDiv: dims?.inoutDiv,
      purpose: dims?.purpose || '집행',
    });
    const payload = rows.length ? mapApiRowToStructurePayload(rows[0], dims) : null;
    structureCache.set(key, payload);
    return payload;
  }
  const payload = readStructureFromStorage(dims);
  structureCache.set(key, payload);
  return payload;
}

export async function saveStructurePayload(dims, payload) {
  const key = structureStorageKey(dims);
  structureCache.set(key, payload);

  if (isBdApiEnabled()) {
    const existing = await commonSelect('BdSubject.selectSubjectStructureList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      inoutDiv: dims?.inoutDiv,
      purpose: dims?.purpose || '집행',
    });
    const rows = normalizeStructureGridRows(payload?.rows);
    const levelsJson = JSON.stringify(rows);
    const purpose = dims?.purpose || '집행';
    // 구조화면은 예산구분을 쓰지 않음. DB UK(budget_type NOT NULL) → 사업/자본에 동일 구조 저장
    const types = resolveBudgetTypes(dims?.budgetType);
    const saveRows = types.map((budgetType) => {
      const found = existing.find((r) => r.budgetType === budgetType);
      return {
        structureUid: found?.structureUid || createStructureUid(),
        inoutDiv: dims?.inoutDiv,
        purpose,
        budgetType,
        levelsJson,
        applyLevelName: payload?.applyLevelName || payload?.finalLevelName || null,
        confirmYn: payload?.status === '확정' ? 'Y' : 'N',
        confirmedBy: payload?.confirmedBy || null,
        confirmedAt: toSqlTimestamp(payload?.confirmedAt),
        versionNo: Number(payload?.versionNo) || 1,
        mod: found ? 'U' : 'A',
      };
    });
    await commonSaveList(STRUCTURE_SAVE_QUERY, saveRows, {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    structureCache.set(key, { ...payload, structureUid: saveRows[0]?.structureUid });
    return key;
  }

  localStorage.setItem(key, JSON.stringify(payload));
  localStorage.setItem(`${STRUCTURE_STORAGE_PREFIX}:lastKey`, key);
  return key;
}

export async function countSubjects(dims) {
  // 구조 확정해제: 동일 기관·연도·수지·목적의 사업/자본 과목 합산
  const rows = await loadSubjectsByFilter({ ...dims, budgetType: BUDGET_TYPE_ALL });
  return rows.length;
}

export async function canUnconfirmStructure(dims) {
  const subjectCount = await countSubjects(dims);
  const purposeLabel = dims.purpose === '보고' ? '보고(예산서)' : '내부집행';
  if (subjectCount > 0) {
    return {
      ok: false,
      subjectCount,
      reason: `[${purposeLabel}] 목적의 예산과목이 ${subjectCount}건 있어 확정해제할 수 없습니다. 이미 등록된 과목이 이 구조에 묶여 있습니다. 차기년도 복사를 사용하세요.`,
    };
  }
  return { ok: true, subjectCount: 0 };
}

export function getConfirmedStructure(dims) {
  const data = getCachedStructurePayload(dims);
  if (!data || data.status !== '확정') return null;
  const used = data.usedLevels?.length ? data.usedLevels : getUsedLevels(data.rows || []);
  if (!used.length) return null;
  const acctMapNames =
    data.acctMapLevelNames?.length > 0
      ? data.acctMapLevelNames
      : used.filter((r) => r.acctMapYn === 'Y').map((r) => r.levelName);
  return {
    dims: { ...dims, purpose: dims.purpose || data.dims?.purpose || '집행' },
    status: data.status,
    purpose: data.purpose || dims.purpose || '집행',
    purposeCode: PURPOSE_CODE[data.purpose || dims.purpose || '집행'] || 'EXEC',
    usedLevels: used,
    finalLevelName: data.finalLevelName || used.find((r) => r.finalYn === 'Y')?.levelName || '',
    acctMapLevelNames: acctMapNames,
    rows: data.rows || [],
    versionNo: data.versionNo || 1,
    confirmedAt: data.confirmedAt || null,
    confirmedBy: data.confirmedBy || null,
    startDate: data.startDate,
    endDate: data.endDate,
  };
}

export function buildBudgetLevelColumnFields(usedLevels = []) {
  return (usedLevels || []).map((lv, idx) => ({
    field: `lv${lv.levelNo ?? idx + 1}`,
    header: lv.levelName,
    levelNo: Number(lv.levelNo ?? idx + 1),
    levelCode: lv.levelCode,
    finalYn: lv.finalYn === 'Y',
    acctMapYn: lv.acctMapYn === 'Y',
  }));
}

export async function copyStructureToNextYear(dims, { userId } = {}) {
  const data = await loadStructurePayload(dims);
  if (!data?.rows?.length) return { ok: false, reason: '복사할 구조가 없습니다.' };
  const nextYear = String(Number(dims.fiscalYear) + 1);
  if (!Number.isFinite(Number(dims.fiscalYear))) {
    return { ok: false, reason: '회계년도가 올바르지 않습니다.' };
  }
  const nextDims = { ...dims, fiscalYear: nextYear };
  const existing = await loadStructurePayload(nextDims);
  if (existing?.status === '확정') {
    return { ok: false, reason: `${nextYear}년 구조가 이미 확정되어 있습니다.` };
  }
  const payload = {
    ...data,
    dims: nextDims,
    status: '작성중',
    versionNo: (Number(data.versionNo) || 1) + 1,
    startDate: `${nextYear}-01-01`,
    endDate: `${nextYear}-12-31`,
    confirmedAt: null,
    confirmedBy: null,
    unconfirmHistory: data.unconfirmHistory || [],
    copiedFrom: { ...dims, versionNo: data.versionNo || 1 },
    savedAt: new Date().toISOString(),
    savedBy: userId || '',
  };
  await saveStructurePayload(nextDims, payload);
  return { ok: true, nextDims, payload };
}

function readSubjectsFromStorage(dims) {
  try {
    const raw = localStorage.getItem(subjectStorageKey(dims));
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

/** 과목 속성(attr_json) ↔ flat 필드 */
function mapApiRowToSubject(row) {
  let attrs = {};
  try {
    if (row?.attrJson) attrs = JSON.parse(row.attrJson) || {};
  } catch {
    attrs = {};
  }
  return { ...attrs, ...row, mod: '' };
}

function subjectPersistPayload(row, dims, budgetType) {
  const {
    mod,
    _tempId,
    id,
    tenantId,
    tenant_id: _tid,
    tenantCode: _tc,
    tenant_code: _tcode,
    version,
    attrJson: _drop,
    subjectUid,
    subjectCode,
    subjectName,
    parentCode,
    levelName,
    pathCode,
    finalYn,
    fundUid,
    sortOrder,
    useYn,
    remark,
    fiscalYear: _fy,
    inoutDiv: _io,
    purpose: _pp,
    budgetType: _bt,
    ...attrs
  } = row || {};
  return {
    subjectUid: subjectUid || createStructureUid('SJ'),
    inoutDiv: dims?.inoutDiv,
    purpose: dims?.purpose || '집행',
    budgetType,
    subjectCode,
    subjectName,
    parentCode: parentCode || null,
    levelName: levelName || null,
    pathCode: pathCode || null,
    finalYn: finalYn || 'N',
    fundUid: fundUid || null,
    attrJson: Object.keys(attrs).length ? JSON.stringify(attrs) : null,
    sortOrder,
    useYn: useYn || 'Y',
    remark: remark || null,
    mod,
  };
}

/** 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedSubjectsByFilter(dims) {
  const types = resolveBudgetTypes(dims?.budgetType);
  const out = [];
  types.forEach((budgetType) => {
    const key = subjectStorageKey({ ...dims, budgetType });
    let rows = cacheGet(subjectCache, key);
    if (!rows) {
      if (isBdApiEnabled()) return;
      rows = readSubjectsFromStorage({ ...dims, budgetType });
      cacheSet(subjectCache, key, rows);
    }
    rows.forEach((r) => out.push({ ...r, budgetType: r.budgetType || budgetType }));
  });
  return ensureSubjectsIdentity(out);
}

export async function loadSubjects(dims) {
  const bt = isBudgetTypeAll(dims?.budgetType)
    ? STRUCTURE_BUDGET_TYPE
    : dims?.budgetType || STRUCTURE_BUDGET_TYPE;
  const key = subjectStorageKey({ ...dims, budgetType: bt });
  if (isBdApiEnabled()) {
    const rows = await commonSelect('BdSubject.selectSubjectList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      inoutDiv: dims?.inoutDiv,
      purpose: dims?.purpose || '집행',
      budgetType: bt,
    });
    const mapped = rows.map((r) => mapApiRowToSubject(r));
    cacheSet(subjectCache, key, mapped);
    return mapped;
  }
  const rows = readSubjectsFromStorage({ ...dims, budgetType: bt });
  cacheSet(subjectCache, key, rows);
  return rows;
}

/** 검색 예산구분에 따라 사업/자본(또는 전체) 과목을 병합 로드 */
export async function loadSubjectsByFilter(dims) {
  const types = resolveBudgetTypes(dims.budgetType);
  const out = [];
  for (const budgetType of types) {
    const rows = await loadSubjects({ ...dims, budgetType });
    rows.forEach((r) => out.push({ ...r, budgetType: r.budgetType || budgetType }));
  }
  // 세목 10101 표기·세항 기준 경로코드 정규화
  return ensureSubjectsIdentity(out);
}

export async function saveSubjects(dims, rows) {
  const bt = isBudgetTypeAll(dims.budgetType) ? STRUCTURE_BUDGET_TYPE : dims.budgetType || STRUCTURE_BUDGET_TYPE;
  const key = subjectStorageKey({ ...dims, budgetType: bt });

  if (isBdApiEnabled()) {
    const list = rows || [];
    const hasMod = list.some((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
    const existing = await commonSelect('BdSubject.selectSubjectList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      inoutDiv: dims?.inoutDiv,
      purpose: dims?.purpose || '집행',
      budgetType: bt,
    });
    const byUid = new Map(existing.map((r) => [String(r.subjectUid || ''), r]));
    const byCode = new Map(
      existing.map((r) => [String(r.subjectCode || '').trim(), r]).filter(([c]) => c)
    );
    const resolveRow = (r) => {
      const p = subjectPersistPayload(r, dims, bt);
      if (r.mod === 'D') return { ...p, mod: 'D' };
      const found =
        (p.subjectUid && byUid.get(String(p.subjectUid))) ||
        byCode.get(String(p.subjectCode || '').trim());
      // UK: tenant+연도+수지+목적+예산구분+과목코드 — uid가 달라도 코드가 있으면 UPDATE
      if (found) return { ...p, subjectUid: found.subjectUid, mod: 'U' };
      return { ...p, mod: 'A' };
    };
    const payload = hasMod
      ? list.filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D')).map(resolveRow)
      : list.map(resolveRow);
    await commonSaveList(SUBJECT_SAVE_QUERY, payload, {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
    });
    return loadSubjects({ ...dims, budgetType: bt });
  }

  localStorage.setItem(
    key,
    JSON.stringify({
      dims: { ...dims, budgetType: bt },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
  cacheSet(subjectCache, key, rows || []);
  return rows || [];
}

/**
 * 그리드 저장: ‘전체’면 행의 budgetType별로 나눠 저장
 * @returns {{ ok: boolean, reason?: string }}
 */
export async function saveSubjectsByFilter(dims, rows) {
  const list = rows || [];
  if (!isBudgetTypeAll(dims.budgetType)) {
    const bt = dims.budgetType || STRUCTURE_BUDGET_TYPE;
    const normalized = list.map((r) => ({ ...r, budgetType: r.budgetType || bt }));
    await saveSubjects({ ...dims, budgetType: bt }, normalized);
    return { ok: true };
  }
  const missing = list.find((r) => !r.budgetType || isBudgetTypeAll(r.budgetType));
  if (missing) {
    return { ok: false, reason: '전체 조회에서는 각 행의 예산구분(사업예산/자본예산)이 필수입니다.' };
  }
  for (const budgetType of BUDGET_TYPE_CONCRETE) {
    const part = list.filter((r) => r.budgetType === budgetType);
    await saveSubjects({ ...dims, budgetType }, part);
  }
  return { ok: true };
}

/**
 * 과목 트리 구성. 예산구분이 섞이면 코드 충돌을 피하기 위해 budgetType+코드를 키로 사용.
 */
export function buildSubjectTree(flatRows = [], { idKey = 'subjectCode', parentKey = 'parentCode', childrenKey = 'subRows' } = {}) {
  const norm = (v) => (v == null || v === '' ? '' : String(v).trim());
  const types = new Set((flatRows || []).map((r) => r.budgetType).filter(Boolean));
  const multi = types.size > 1;
  const nodeId = (r) => {
    const code = norm(r[idKey]);
    if (!code) return '';
    return multi ? `${norm(r.budgetType)}::${code}` : code;
  };
  const parentId = (r) => {
    const pid = norm(r[parentKey]);
    if (!pid) return '';
    return multi ? `${norm(r.budgetType)}::${pid}` : pid;
  };

  const map = new Map();
  const roots = [];
  for (const r of flatRows) {
    const id = nodeId(r);
    if (!id) continue;
    map.set(id, { ...r, _treeId: id, [childrenKey]: [] });
  }
  for (const [, node] of map) {
    const pid = parentId(node);
    const parent = pid ? map.get(pid) : null;
    if (parent) parent[childrenKey].push(node);
    else roots.push(node);
  }
  return roots;
}

export function flattenSubjectTree(nodes = [], childrenKey = 'subRows') {
  const out = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      const { [childrenKey]: children, ...rest } = n;
      out.push(rest);
      walk(children);
    });
  };
  walk(nodes);
  return out;
}
